<template>
  <v-dialog :value="dialogProp.show" persistent max-width="1000">
    <v-card class="elevation-12">
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-toolbar-title>
          {{ $t('Отзыв торговой процедуры') }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || isLoading" class="my-0 py-0 mx-1">
        <v-col cols="12">
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
        </v-col>
        <v-col class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-card-text class="pt-3">
        <v-row>
          <v-col>
            <request-result-info
                :request-type="requestType"
                :process-comment="request.processComment"
                :request-status="request.status"
                :organization-id="request.organizationId" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <lot-info-view :lot="lot" attachments>
              <template v-slot:attachments>
                <business-document-list
                    ref="requestFiles"
                    :request-files="requestFiles"
                    :document-types="requiredDocumentTypes"
                    :validation-required="false"
                    :readonly="readonly"
                    :loading="isLoading"
                    :lot-request-info="lotRequestInfo"
                    @deleted="getLotRequestFiles"
                    @uploaded="attachFileToLotRequest" />
              </template>
            </lot-info-view>
          </v-col>
        </v-row>
        <v-row>
          <v-col mt-3>
            <ValidationObserver ref="comment">
              <validatable-textarea
                  v-model="processComment"
                  :readonly="readonly"
                  :label="$t('Комментарий')"
                  rules="required" />
            </ValidationObserver>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
            id="cancelBtn"
            v-test="{ btn: 'cancel' }"
            color="primary"
            class="mx-2"
            @click.stop="cancel">
          {{ $t('Отмена') }}
        </v-btn>
        <v-btn
            id="continueBtn"
            v-test="{ btn: 'continue' }"
            :disabled="readonly"
            color="primary"
            @click.stop="createLotRequest">
          {{ $t('Продолжить') }}
        </v-btn>

      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import Vue from 'vue'
  import LotInfoView from './LotInfoView.vue'
  import BusinessDocumentList from '@/components/reused/files/BusinessDocumentList.vue'
  import RequestResultInfo from './RequestResultInfo.vue'
  import ValidatableTextarea from '@/components/reused/ui/ValidatableTextarea.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { REQUEST_STATUSES, LOT_REQUEST_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { LOT_MANAGEMENT, TRADE_REQUEST_MANAGEMENT, LOT_REQUESTS_MANAGEMENT, BUSINESS_FILES } from '@/constants/store.js'

  export default {
    components: {
      LotInfoView,
      BusinessDocumentList,
      RequestResultInfo,
      ValidatableTextarea,
      GeneralError
    },
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            lotId: null,
            lotType: null,
            show: false,
            callback: null
          }
        }
      }
    },
    data () {
      return {
        isLoading: false,
        requestType: LOT_REQUEST_TYPES.RECALL,
        processComment: ''
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        request: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.LAST] || {})
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        requestFiles: state => (state[BUSINESS_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[BUSINESS_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      lotRequestInfo () {
        return {
          lotId: this.dialogProp.lotId,
          lotType: this.dialogProp.lotType,
          requestType: this.requestType,
          creatorId: this.lot.creatorId
        }
      },
      readonly () {
        return !this.lot.lotId ||
          !this.canSendRequest ||
          this.isLoading
      },
      canSendRequest () {
        const status = this.request.status
        if (status === REQUEST_STATUSES.INPROCESS ||
          status === REQUEST_STATUSES.APPROVED) {
          return false
        }
        return true
      },
      lot () {
        return this.currentLot || {
          lotId: this.dialogProp.lotId,
          lotType: this.dialogProp.lotType
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.dialogProp.lotId &&
          vm.dialogProp.lotType &&
          vm.requestType) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        getLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LAST_LOT_REQUEST_INFO,
        clearLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CLEAR_LAST_LOT_REQUEST_INFO
      }),
      ...mapActions(TRADE_REQUEST_MANAGEMENT.NAMESPACE, {
        recallLotAction: TRADE_REQUEST_MANAGEMENT.ACTIONS.RECALL_LOT
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        getLotRequestFilesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_FILES,
        getLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES,
        attachFileToLotRequestAction: BUSINESS_FILES.ACTIONS.ATTACH_FILE_TO_LOT_REQUEST,
        clearLotRequestFilesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_FILES,
        clearLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES
      }),
      getLotRequestFiles: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await this.getLotRequestFilesAction(model)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearLastLotRequestInfoAction(),
            this.clearLotRequestRequiredDocumentTypesAction(),
            this.clearLotRequestFilesAction()
          ])
      },
      initialize: async function () {
        const model = {
          requestType: this.requestType,
          lotType: this.lot.lotType,
          lotId: this.lot.lotId
        }
        try {
          this.isLoading = true
          await Promise.all([
            this.getLotInfoAction(model),
            this.getLastLotRequestInfoAction(model),
            this.getLotRequestRequiredDocumentTypesAction(model),
            this.getLotRequestFiles()
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      attachFileToLotRequest: async function (file) {
        this.isLoading = true

        try {
          await this.attachFileToLotRequestAction({
            requestType: this.requestType,
            lotId: this.lot.lotId,
            lotType: this.lot.lotType,
            fileId: file.fileId
          })
          await this.getLotRequestFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      validateRequestFiles: async function () {
        const result = await this.$refs.comment.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Комментарий обязателен для заполнения.'))
        }
        if (this.requestFiles.length) {
          await this.$refs.requestFiles.validate()
        }
      },
      createLotRequest: async function (signedFiles) {
        try {
          if (!signedFiles.length) {
            signedFiles = []
            await this.validateRequestFiles()
          }
          this.isLoading = true
          await this.recallLotAction({
            request: {
              documents: signedFiles || [],
              processComment: this.processComment
            },
            lotId: this.dialogProp.lotId
          })
          this.isLoading = false
          if (this.dialogProp.callback) {
            this.dialogProp.callback()
            this.cancel()
          }
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      cancel: function () {
        this.$emit('close')
      }
    }
  }
</script>
